// File: /opt/stacks/workout/app/src/config/soundProfiles.js
//
// Sound profile definitions for the workout audio cue system.
// Each sound is a sequence of notes and rests scheduled by Tone.js.
//
// Sound config shape:
//   [{ note: "C4" | 440, duration: 0.1 }, { rest: 0.05 }]
//
// Each set also has `voices`: the instruments its notes play through, each
// with its options and a level in dB. `main` is the default; a note picks
// another with `voice`. `synth` and `fm` are Tone.js synths; bell, whistle,
// bugle, wood, drum, mallet and clap are built in audio/instruments.js, and a
// note can pass them vel, damp, roll or bend. See audio/scheduleCue.js.
//
// Levels were matched by rendering every cue offline and measuring its
// loudness through a phone-speaker-like filter: Work, Rest and Set rest land
// near -10 LUFS in every set (Dojo's woodblocks a little under), so switching
// set doesn't change the volume much. Short percussive ticks (clack, clap,
// claves) are set by their peak instead, which reaches full scale long
// before a 40 ms click measures as loud.
//
// In every set, Work and Rest are easy to tell apart, Set rest is a longer
// version of Rest, and the Tick is a single short note unlike any phase cue.
// Most sets do it with pitch (Work rises, Rest falls); Boxing Gym and Referee
// do it the way the real thing does, by the number of strikes or blasts.

// Partials of a boxing ring bell: [ratio, level, share of the ring time].
const RING_BELL = [
  [1, 1, 1],
  [1.004, 0.7, 1], // a near-unison partial; the slow beat makes it shimmer
  [2.32, 0.55, 0.6],
  [4.25, 0.3, 0.4],
  [6.63, 0.15, 0.25],
  [9.38, 0.08, 0.15],
];
const BELL = 1180; // Hz
const WHISTLE = 2850; // Hz

export const LEGACY_PROFILE_KEYS = {
  chiptune_mario: "original",
  chiptune: "original",
};

// A timed phase must exceed this many seconds for the "halfway" midway beep to
// fire at its midpoint. Shared by the solo coach (useWorkoutCoach) and the live
// follower (useLiveFollower) so both paths stay in lock-step.
export const MIDWAY_BEEP_MIN_SECONDS = 10;

export function resolveSoundProfileKey(profileKey) {
  return LEGACY_PROFILE_KEYS[profileKey] || profileKey;
}

export const SOUND_PROFILES = {
  electronic: {
    label: "Electronic",
    description: "Sharp synth tones that cut through gym noise.",
    voices: {
      main: {
        instrument: "synth",
        volume: -3.5,
        options: {
          oscillator: { type: "fmsquare", modulationType: "sawtooth" },
          envelope: { attack: 0.005, decay: 0.08, sustain: 0.6, release: 0.06 },
        }
      },
    },

    sounds: {
      work: [
        { note: 659.25, duration: 0.1 },
        { rest: 0.12 },
        { note: 880, duration: 0.1 },
      ],
      rest: [
        { note: 880, duration: 0.1 },
        { rest: 0.05 },
        { note: 659.25, duration: 0.1 },
        { rest: 0.05 },
        { note: 330, duration: 0.1 },
      ],
      set_rest: [
        { note: 523.25, duration: 0.1 },
        { rest: 0.06 },
        { note: 392, duration: 0.12 },
        { rest: 0.06 },
        { note: 330, duration: 0.16 },
      ],
      warmup: [
        { note: 392, duration: 0.15 },
        { rest: 0.18 },
        { note: 440, duration: 0.15 },
        { rest: 0.18 },
        { note: 523.25, duration: 0.15 },
      ],
      cooldown: [
        { note: 523.25, duration: 0.2 },
        { rest: 0.2 },
        { note: 392, duration: 0.2 },
        { rest: 0.2 },
        { note: 330, duration: 0.2 },
      ],
      end: [
        // Triumphant ascending fanfare
        { note: 523.25, duration: 0.1 },
        { rest: 0.08 },
        { note: 659.25, duration: 0.1 },
        { rest: 0.08 },
        { note: 783.99, duration: 0.1 },
        { rest: 0.08 },
        { note: 1046.5, duration: 0.15 },
        { rest: 0.15 },
        { note: 783.99, duration: 0.08 },
        { rest: 0.04 },
        { note: 1046.5, duration: 0.3 },
      ],
      countdown_tick: [{ note: 880, duration: 0.08 }],
      pause: [{ note: 300, duration: 0.1 }],
      resume: [
        { note: 440, duration: 0.08 },
        { rest: 0.04 },
        { note: 659.25, duration: 0.1 },
      ],
      halfway: [
        { note: 660, duration: 0.06 },
        { rest: 0.1 },
        { note: 660, duration: 0.06 },
      ],
    },
  },

  boxing: {
    label: "Boxing Gym",
    description: "A ring bell: three dings to start, one to rest.",
    voices: {
      main: {
        instrument: "bell",
        volume: -7.5,
        options: { partials: RING_BELL, ring: 2.2 },
      },
      clack: {
        instrument: "wood",
        volume: 1,
        options: { decay: 0.07, upper: 2.27, upperLevel: 0.35, upperDecay: 0.03, click: 0.25 },
      },
    },
    sounds: {
      work: [
        { note: BELL, duration: 0.26 },
        { note: BELL, duration: 0.26 },
        { note: BELL, duration: 0.3 },
      ],
      rest: [
        { note: BELL, duration: 0.3 },
      ],
      set_rest: [
        { note: BELL, duration: 0.9 },
        { note: BELL, duration: 0.3, vel: 0.7 },
      ],
      warmup: [
        { note: BELL, duration: 0.5, vel: 0.6 },
        { note: BELL, duration: 0.3, vel: 0.6 },
      ],
      cooldown: [
        { note: BELL, duration: 0.3, vel: 0.6 },
      ],
      end: [
        // The electric ring at the end of a round, then one clean ding
        { note: BELL, duration: 1.5, roll: 0.055 },
        { rest: 0.35 },
        { note: BELL, duration: 0.3 },
      ],
      countdown_tick: [{ note: 1850, duration: 0.04, voice: "clack" }],
      pause: [
        { note: 1850, duration: 0.12, voice: "clack" },
        { note: 1850, duration: 0.04, voice: "clack" },
      ],
      resume: [
        { note: BELL, duration: 0.3, vel: 0.7 },
      ],
      halfway: [
        { note: BELL, duration: 0.08, vel: 0.6, damp: true },
        { rest: 0.08 },
        { note: BELL, duration: 0.08, vel: 0.6, damp: true },
      ],
    },
  },

  minimal: {
    label: "Minimal",
    description: "Soft marimba notes. Distraction-free.",
    voices: {
      main: { instrument: "mallet", volume: -3 },
    },
    sounds: {
      work: [
        { note: "C5", duration: 0.11 },
        { note: "E5", duration: 0.11 },
        { note: "G5", duration: 0.11 },
      ],
      rest: [
        { note: "G5", duration: 0.13 },
        { note: "E5", duration: 0.13 },
        { note: "C5", duration: 0.13 },
      ],
      set_rest: [
        { note: "G5", duration: 0.2 },
        { note: "E5", duration: 0.2 },
        { note: "C5", duration: 0.2 },
        { note: "G4", duration: 0.2 },
      ],
      warmup: [
        { note: "C5", duration: 0.15 },
        { note: "E5", duration: 0.15 },
      ],
      cooldown: [
        { note: "E5", duration: 0.15 },
        { note: "C5", duration: 0.15 },
      ],
      end: [
        { note: "C5", duration: 0.12 },
        { note: "E5", duration: 0.12 },
        { note: "G5", duration: 0.12 },
        { note: "C6", duration: 0.14 },
        { note: "C6", duration: 0.3 },
      ],
      countdown_tick: [
        { note: "G6", duration: 0.05 },
      ],
      pause: [
        { note: "G4", duration: 0.1, vel: 0.7 },
      ],
      resume: [
        { note: "C5", duration: 0.1, vel: 0.8 },
        { note: "G5", duration: 0.1, vel: 0.8 },
      ],
      halfway: [
        { note: "G5", duration: 0.1, vel: 0.6 },
      ],
    },
  },

  // The sounds from the owner's first timer (stored as "chiptune" before
  // v0.169.0; LEGACY_PROFILE_KEYS maps it here).
  original: {
    label: "Original",
    description: "The sounds from the first Khronos timer.",
    voices: {
      main: {
        instrument: "synth",
        volume: -3,
        options: {
          oscillator: { type: "square" },
          envelope: { attack: 0.003, decay: 0.05, sustain: 0.7, release: 0.03 },
        }
      },
    },

    sounds: {
      work: [
        // Mapped from beepWork
        { note: "C4", duration: 0.075 },
        { note: "E4", duration: 0.075 },
        { note: "G4", duration: 0.075 },
        { rest: 0.1 },
        { note: "C4", duration: 0.075 },
        { note: "E4", duration: 0.075 },
        { note: "G4", duration: 0.075 },
        { rest: 0.1 },
        { note: "C4", duration: 0.075 },
        { note: "E4", duration: 0.075 },
        { note: "G4", duration: 0.075 },
        { rest: 0.1 },
        { note: "C4", duration: 0.075 },
        { note: "E4", duration: 0.075 },
        { note: "G4", duration: 0.075 },
      ],
      rest: [
        // Mapped from beepRest
        { note: "G4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { rest: 0.15 },
        { note: "G4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { rest: 0.3 },
        { note: "G4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { rest: 0.15 },
        { note: "G4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "C4", duration: 0.1 },
      ],
      warmup: [
        { note: "G4", duration: 0.12 },
        { rest: 0.06 },
        { note: "E4", duration: 0.12 },
        { rest: 0.06 },
        { note: "C4", duration: 0.2 },
      ],
      // Original had no cool-down or halfway cue, so both played silence.
      cooldown: [
        { note: "E4", duration: 0.12 },
        { rest: 0.06 },
        { note: "C4", duration: 0.12 },
        { rest: 0.06 },
        { note: "G3", duration: 0.25 },
      ],
      halfway: [{ note: "C6", duration: 0.06 }],
      set_rest: [
        // Mapped from beepSetRest
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
        { rest: 0.15 },
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
        { rest: 0.3 },
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
        { rest: 0.15 },
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
        { rest: 0.3 },
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
        { rest: 0.15 },
        { note: "G4", duration: 0.15 },
        { note: "E4", duration: 0.15 },
        { note: "C4", duration: 0.15 },
      ],
      countdown_tick: [
        // Mapped from beepCntDwn
        { note: "G5", duration: 0.25 },
      ],
      pause: [
        // Mapped from beepPause
        { note: "E6", duration: 0.1 },
        { note: "C6", duration: 0.1 },
        { note: "E6", duration: 0.1 },
        { note: "C6", duration: 0.1 },
      ],
      resume: [
        { note: "C5", duration: 0.07 },
        { rest: 0.03 },
        { note: "E5", duration: 0.07 },
        { rest: 0.03 },
        { note: "G5", duration: 0.12 },
      ],
      end: [
        // Mapped from melodyFlagpole (Super Mario Bros. style level complete)
        { note: "G3", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "G4", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "E5", duration: 0.1 },
        { note: "G5", duration: 0.3 },
        { note: "E5", duration: 0.3 },
        { note: "G#3", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { note: "Eb4", duration: 0.1 },
        { note: "G#4", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "Eb5", duration: 0.1 },
        { note: "G#5", duration: 0.3 },
        { note: "Eb5", duration: 0.3 },
        { note: "Bb3", duration: 0.1 },
        { note: "D4", duration: 0.1 },
        { note: "F4", duration: 0.1 },
        { note: "Bb4", duration: 0.1 },
        { note: "D5", duration: 0.1 },
        { note: "F5", duration: 0.1 },
        { note: "Bb5", duration: 0.3 },
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        { note: "C6", duration: 0.6 },
      ],
      ui_click_short: [
        // Mapped from beepClickShrt
        { note: "C4", duration: 0.02 },
      ],

      ui_click: [
        // Mapped from beepClick
        { note: "E4", duration: 0.05 },
      ],

      ui_coin: [
        // Mapped from beepCoin (Great for achieving a goal / unlocking something)
        { note: "B5", duration: 0.1 },
        { note: "E6", duration: 0.4 },
      ],

      ui_coin_reverse: [
        // Mapped from beepCoinReverse (Great for an undo or cancel action)
        { note: "E6", duration: 0.1 },
        { note: "B5", duration: 0.4 },
      ],

      melody_flagpole: [
        // Mapped from melodyFlagpole (Super Mario Bros. style level complete)
        // I - C
        { note: "G3", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { note: "E4", duration: 0.1 },
        { note: "G4", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "E5", duration: 0.1 },
        { note: "G5", duration: 0.3 },
        { note: "E5", duration: 0.3 },
        // Flat(VI) (min) - Ab
        { note: "G#3", duration: 0.1 },
        { note: "C4", duration: 0.1 },
        { note: "Eb4", duration: 0.1 },
        { note: "G#4", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "Eb5", duration: 0.1 },
        { note: "G#5", duration: 0.3 },
        { note: "Eb5", duration: 0.3 },
        // VII - Bb
        { note: "Bb3", duration: 0.1 },
        { note: "D4", duration: 0.1 },
        { note: "F4", duration: 0.1 },
        { note: "Bb4", duration: 0.1 },
        { note: "D5", duration: 0.1 },
        { note: "F5", duration: 0.1 },
        { note: "Bb5", duration: 0.3 },
        // Staccato ascending finish
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        { rest: 0.02 },
        { note: "Bb5", duration: 0.08 },
        // I - C
        { note: "C6", duration: 0.6 },
      ],

      melody_death: [
        // Mapped from melodyDeath (Super Mario Bros. style death/fail)
        { note: "C6", duration: 0.04 },
        { note: "C#6", duration: 0.04 },
        { note: "D6", duration: 0.04 },
        { rest: 0.36 },
        { note: "B5", duration: 0.15 },
        { note: "F6", duration: 0.15 },
        { rest: 0.15 },
        { note: "F6", duration: 0.145 },
        { rest: 0.005 },
        { note: "F6", duration: 0.09 },
        { rest: 0.075 },
        { note: "E6", duration: 0.09 },
        { rest: 0.075 },
        { note: "D6", duration: 0.09 },
        { rest: 0.075 },
        { note: "C6", duration: 0.12 },
        { note: "E5", duration: 0.12 },
        { rest: 0.12 },
        { note: "E5", duration: 0.12 },
        { note: "C5", duration: 0.12 },
      ],
    },
  },

  referee: {
    label: "Referee",
    description: "A referee's whistle: two blasts to work, one long to rest.",
    voices: {
      main: { instrument: "whistle", volume: -8 },
      clap: { instrument: "clap", volume: 14 },
    },
    sounds: {
      work: [
        { note: WHISTLE, duration: 0.16 },
        { rest: 0.08 },
        { note: WHISTLE, duration: 0.2, bend: 1.03 },
      ],
      rest: [
        { note: WHISTLE, duration: 0.7, bend: 0.97 },
      ],
      set_rest: [
        { note: WHISTLE, duration: 1.2, bend: 0.95 },
      ],
      warmup: [
        { note: WHISTLE, duration: 0.35, bend: 1.02 },
      ],
      cooldown: [
        { note: WHISTLE, duration: 0.5, bend: 0.96 },
      ],
      end: [
        { note: WHISTLE, duration: 0.15 },
        { rest: 0.1 },
        { note: WHISTLE, duration: 0.15 },
        { rest: 0.1 },
        { note: WHISTLE, duration: 0.9, bend: 1.04 },
      ],
      countdown_tick: [{ note: 1400, duration: 0.05, voice: "clap" }],
      pause: [
        { note: 1400, duration: 0.12, voice: "clap" },
        { note: 1400, duration: 0.05, voice: "clap" },
      ],
      resume: [
        { note: WHISTLE, duration: 0.15 },
      ],
      halfway: [
        { note: WHISTLE, duration: 0.08 },
        { rest: 0.08 },
        { note: WHISTLE, duration: 0.08 },
      ],
    },
  },

  stopwatch: {
    label: "Stopwatch",
    description: "Sharp digital beeps, like a sports timer.",
    voices: {
      main: {
        instrument: "synth",
        volume: -11,
        options: {
          oscillator: { type: "square" },
          envelope: { attack: 0.001, decay: 0.001, sustain: 1, release: 0.005 },
        },
      },
      tick: {
        instrument: "synth",
        volume: -5,
        options: {
          oscillator: { type: "square" },
          envelope: { attack: 0.001, decay: 0.001, sustain: 1, release: 0.005 },
        },
      },
    },
    sounds: {
      work: [
        { note: 2730, duration: 0.07 },
        { rest: 0.05 },
        { note: 2730, duration: 0.07 },
        { rest: 0.05 },
        { note: 3640, duration: 0.25 },
      ],
      rest: [
        { note: 2050, duration: 0.5 },
      ],
      set_rest: [
        { note: 2050, duration: 0.45 },
        { rest: 0.15 },
        { note: 2050, duration: 0.45 },
      ],
      warmup: [
        { note: 2730, duration: 0.07 },
        { rest: 0.05 },
        { note: 3640, duration: 0.12 },
      ],
      cooldown: [
        { note: 3640, duration: 0.07 },
        { rest: 0.05 },
        { note: 2050, duration: 0.2 },
      ],
      end: [
        // A watch alarm: three bursts of four
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { rest: 0.3 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { rest: 0.3 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
        { note: 4100, duration: 0.045 },
        { rest: 0.03 },
      ],
      countdown_tick: [{ note: 2730, duration: 0.045, voice: "tick" }],
      pause: [
        { note: 2050, duration: 0.1 },
      ],
      resume: [
        { note: 2730, duration: 0.05 },
        { rest: 0.03 },
        { note: 3640, duration: 0.08 },
      ],
      halfway: [
        { note: 3640, duration: 0.04 },
        { rest: 0.06 },
        { note: 3640, duration: 0.04 },
      ],
    },
  },

  // A valveless bugle only has the notes of its harmonic series, so every
  // call is built from G4, C5, E5, G5 and C6.
  bugle: {
    label: "Bugle",
    description: "Bugle calls: a rising charge to work, a falling call to rest.",
    voices: {
      main: { instrument: "bugle", volume: -9.5 },
      tick: { instrument: "bugle", volume: -3 },
    },
    sounds: {
      work: [
        // Charge!
        { note: "G4", duration: 0.12 },
        { note: "C5", duration: 0.12 },
        { note: "E5", duration: 0.12 },
        { note: "G5", duration: 0.3 },
        { note: "E5", duration: 0.12 },
        { note: "G5", duration: 0.45 },
      ],
      rest: [
        { note: "G5", duration: 0.22 },
        { note: "E5", duration: 0.22 },
        { note: "C5", duration: 0.6 },
      ],
      set_rest: [
        { note: "G4", duration: 0.35 },
        { note: "G4", duration: 0.12 },
        { note: "C5", duration: 0.9 },
        { note: "E5", duration: 0.3 },
        { note: "C5", duration: 0.3 },
        { note: "G4", duration: 0.9 },
      ],
      warmup: [
        { note: "C5", duration: 0.15 },
        { note: "E5", duration: 0.15 },
        { note: "G5", duration: 0.4 },
      ],
      cooldown: [
        { note: "E5", duration: 0.2 },
        { note: "C5", duration: 0.2 },
        { note: "G4", duration: 0.5 },
      ],
      end: [
        // A fanfare
        { note: "C5", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "C5", duration: 0.1 },
        { note: "E5", duration: 0.25 },
        { note: "C5", duration: 0.1 },
        { note: "E5", duration: 0.25 },
        { note: "G5", duration: 0.12 },
        { note: "G5", duration: 0.12 },
        { note: "G5", duration: 0.12 },
        { note: "C6", duration: 0.9 },
      ],
      countdown_tick: [{ note: "G5", duration: 0.1, voice: "tick" }],
      pause: [
        { note: "C5", duration: 0.2, vel: 0.8 },
      ],
      resume: [
        { note: "C5", duration: 0.1 },
        { note: "G5", duration: 0.25 },
      ],
      halfway: [
        { note: "E5", duration: 0.1, vel: 0.8 },
        { note: "E5", duration: 0.15, vel: 0.8 },
      ],
    },
  },

  dojo: {
    label: "Dojo",
    description: "Woodblocks and a hand drum.",
    voices: {
      main: { instrument: "wood", volume: 0 },
      claves: {
        instrument: "wood",
        volume: 3,
        options: { decay: 0.09, upperLevel: 0, click: 0.15 },
      },
      drum: { instrument: "drum", volume: -2 },
    },
    sounds: {
      work: [
        { note: 700, duration: 0.13 },
        { note: 900, duration: 0.13 },
        { note: 1150, duration: 0.13 },
      ],
      rest: [
        { note: 1150, duration: 0.16 },
        { note: 900, duration: 0.16 },
        { note: 700, duration: 0.16 },
      ],
      set_rest: [
        { note: 900, duration: 0.2 },
        { note: 700, duration: 0.3 },
        { note: 300, duration: 0.4, voice: "drum" },
        { note: 240, duration: 0.3, voice: "drum" },
      ],
      warmup: [
        { note: 300, duration: 0.3, voice: "drum" },
        { note: 900, duration: 0.15 },
      ],
      cooldown: [
        { note: 900, duration: 0.3 },
        { note: 240, duration: 0.3, voice: "drum" },
      ],
      end: [
        // A drum roll, then a last hit with a block on top
        { note: 320, duration: 0.07, voice: "drum", vel: 0.3 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.34 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.38 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.42 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.46 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.5 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.54 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.58 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.62 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.66 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.7 },
        { note: 320, duration: 0.07, voice: "drum", vel: 0.74 },
        { note: 260, duration: 0, voice: "drum", vel: 0.6 },
        { note: 1150, duration: 0.3 },
      ],
      countdown_tick: [
        { note: 2500, duration: 0.05, voice: "claves" },
      ],
      pause: [
        { note: 240, duration: 0, voice: "drum" },
        { note: 700, duration: 0.2 },
      ],
      resume: [
        { note: 700, duration: 0.12 },
        { note: 1150, duration: 0.12 },
      ],
      halfway: [
        { note: 1150, duration: 0.1 },
      ],
    },
  },
};

export const DEFAULT_PROFILE = "electronic";

// Haptic vibration patterns per sound type (milliseconds).
// navigator.vibrate() silently ignored on unsupported platforms.
export const HAPTIC_PATTERNS = {
  work: [50, 30, 50], // double pulse
  rest: [100], // single gentle
  set_rest: [80, 40, 80], // set-boundary double pulse
  warmup: [30, 20, 30, 20, 30], // triple light
  cooldown: [80], // single medium
  end: [50, 30, 50, 30, 50, 30, 50], // celebration
  countdown_tick: [20], // tiny tick
  pause: [40], // short tap
  resume: [20, 20, 30], // quick restart cue
  halfway: [30, 30, 30], // double tap
};
